import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { useBrowserEventListener } from '../../../../../../hooks';
import { type ActiveIngredientQuantityStepperOverlayProps } from './ActiveIngredientQuantityStepperOverlay.types';

// ─────────────────────────────────────────────────────────────────────────────

export const ActiveIngredientQuantityStepperOverlay = (
  props: ActiveIngredientQuantityStepperOverlayProps,
) => {
  const { isVisible, children, stepperOffsetY, dismiss } = props;

  const ref = useRef<View>(null);

  // ─── Styles ──────────────────────────────────────────────────────────

  const stepperContainerStyles = useMemo<ViewStyle>(
    () => ({ top: stepperOffsetY }),
    [stepperOffsetY],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const closeOnClickOutside = useCallback(
    (event: Event) => {
      const target = event.target;

      if (!target) return;

      const castedRef = ref.current as unknown as HTMLElement;
      const isOutside = castedRef && !castedRef?.contains?.(target as Node);

      if (!isOutside) return;

      dismiss();
    },
    [dismiss],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useBrowserEventListener(window, 'click', closeOnClickOutside, { capture: true }, !isVisible);

  // ─────────────────────────────────────────────────────────────────────

  if (!isVisible) return;

  return (
    <View
      ref={ref}
      style={[styles.stepperContainer, stepperContainerStyles]}
      pointerEvents="box-none"
    >
      {children}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stepperContainer: {
    position: 'absolute',
    top: 0,
    left: '50%',
  },
});
